// #326 (UC-1): das Dokument-Modell der sechs Template-Abschnitts-Masken.
//
// ── DIE INVARIANTE ────────────────────────────────────────────────────────────────────
// Was diese Masken nicht anfassen, kommt unverändert wieder heraus. Nicht „soweit die
// Maske es versteht" — unverändert.
//
// Das ist keine Höflichkeit, sondern Korrektheit: der Master prüft `process.json` mit
// einem STRIKTEN Schema (`ProcessSpec.ts` — jeder unbekannte Schlüssel ist ein 422). Würde
// eine Maske beim Speichern still löschen, was sie nicht kennt, ginge dreierlei auf einmal
// schief: handgeschriebener Inhalt verschwände wortlos, die Beschwerde des Masters
// verschwände mit ihm, und am Tag, an dem das Schema ein Feld dazubekommt, degradierte
// jedes Öffnen-und-Speichern die Definition.
//
// ── WIE ES ERREICHT WIRD — patchen statt neu bauen ────────────────────────────────────
// Der naheliegende Bau ist „JSON in typisierte Objekte lesen, editieren, zurückschreiben",
// und der kann die Invariante prinzipiell nicht halten: beim Serialisieren entstehen genau
// die Felder, die der Typ kennt — alles andere ist per Konstruktion weg. Also wird nichts
// neu gebaut. Jede editierbare Zeile trägt ihren ORIGINAL-Wert (`roh`) mit sich; geschrieben
// wird `roh` plus die tatsächlich geänderten Felder. Eine Zeile, die niemand angefasst hat,
// geht als exakt dasselbe Objekt wieder hinaus — Schlüssel-Reihenfolge inklusive.
//
// `ENTFERNEN` ist, wie ein Patch „dieser Schlüssel soll weg" sagt; `undefined` kann das
// nicht (`JSON.stringify` verschluckt es, „Zweck löschen" täte dann still gar nichts).
// Referenz für das Muster: `ai-client/src/renderer/src/routes/processes/flowSpec.ts` (EL-13).
//
// ── EHRLICHE GRENZE ───────────────────────────────────────────────────────────────────
// Erhalten bleiben WERTE und Schlüssel-Reihenfolge, nicht die Formatierung: geschrieben
// wird immer `JSON.stringify(…, null, 2) + "\n"` — dieselbe Form, die das Scaffold erzeugt
// (`ProcessRegistryService`). Eine von Hand anders eingerückte Datei wird beim ersten
// Speichern auf diese Form gebracht. Kommentare kann eine `.json` ohnehin nicht tragen.
//
// Bewusst OHNE React/DOM: so lässt sich die Invariante in einem reinen Test beweisen statt
// durch eine gerenderte Komponente.

/** Patch-Wert „diesen Schlüssel entfernen" — siehe die Notiz oben zu `undefined`. */
export const ENTFERNEN = Symbol("entfernen");
export type PatchWert = unknown;

export const ABSCHNITTE = ["trigger", "instantiation", "systems", "input", "weisungen", "settings"] as const;
export type AbschnittKey = (typeof ABSCHNITTE)[number];

export function istRecord(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert);
}

/**
 * Roh-Objekt + geänderte Felder, ohne den Rest anzufassen.
 *
 * ⚠ Bewusst `Object.create(null)` als Basis, kein `{}`-Literal: ein Dokument mit dem
 * Schlüssel `__proto__` (den `JSON.parse` als ganz normale eigene Eigenschaft anlegt) ginge
 * hier sonst verloren — die ZUWEISUNG `obj.__proto__ = …` trifft auf einem gewöhnlichen
 * Objekt den geerbten Setter und ändert den Prototyp, statt eine Eigenschaft anzulegen;
 * `JSON.stringify` schriebe den Schlüssel danach nicht mehr. Ohne Prototyp gibt es keinen
 * Setter in der Kette, und die Zuweisung legt eine echte Eigenschaft an. (Ein Spread täte
 * es zufällig auch richtig — er kopiert per CreateDataProperty; die Zuweisung, die hier
 * gebraucht wird, aber nicht.)
 */
export function mitFeldern(roh: unknown, felder: Record<string, PatchWert>): Record<string, unknown> {
  const ergebnis: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  const rohKeys = istRecord(roh) ? Object.keys(roh) : [];
  // Erst die Roh-Schlüssel in ihrer Reihenfolge — ein geändertes Feld bleibt damit an
  // seiner Stelle, statt ans Ende zu wandern.
  for (const k of rohKeys) {
    if (Object.hasOwn(felder, k)) {
      if (felder[k] === ENTFERNEN) continue;
      ergebnis[k] = felder[k];
    } else {
      ergebnis[k] = (roh as Record<string, unknown>)[k];
    }
  }
  // Dann, was neu dazukommt.
  for (const [k, v] of Object.entries(felder)) {
    if (rohKeys.includes(k)) continue;
    if (v === ENTFERNEN) continue;
    ergebnis[k] = v;
  }
  return ergebnis;
}

/** Dokument + Abschnitts-Ersetzungen → der Text, der gespeichert wird. */
export function serialisiereSpec(roh: Record<string, unknown>, ersatz: Record<string, PatchWert>): string {
  return `${JSON.stringify(mitFeldern(roh, ersatz), null, 2)}\n`;
}

export type SpecLeseErgebnis =
  | { readonly ok: true; readonly doc: Record<string, unknown> }
  | { readonly ok: false; readonly fehler: string };

/** Text → Roh-Dokument. Kein Schema-Urteil — das fällt der Master (422 mit Fundstellen). */
export function leseSpecDokument(text: string): SpecLeseErgebnis {
  let json: unknown;
  try {
    json = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, fehler: `Die Definition ist kein gültiges JSON und lässt sich hier nicht bearbeiten. Sie muss im Inhalt der Definition korrigiert werden.` };
  }
  if (!istRecord(json)) {
    return { ok: false, fehler: `Die Definition ist kein Objekt und lässt sich hier nicht bearbeiten. Sie muss im Inhalt der Definition korrigiert werden.` };
  }
  return { ok: true, doc: json };
}

// --- Auslöser -----------------------------------------------------------------------------

/** Die sechs Arten aus `PROCESS_TRIGGER_KINDS` — hier mit ihrem deutschen Namen. */
export const AUSLOESER = [
  { kind: "manual", label: "Manuell (Start-Knopf)", einrichtbar: true },
  { kind: "schedule", label: "Zeitplan", einrichtbar: true },
  { kind: "mail", label: "E-Mail", einrichtbar: false },
  { kind: "webhook", label: "Webhook", einrichtbar: false },
  { kind: "chat", label: "Chat", einrichtbar: false },
  { kind: "a2a", label: "Anderer Agent", einrichtbar: false },
] as const;

export function ausloeserLabel(kind: string): string {
  return AUSLOESER.find((a) => a.kind === kind)?.label ?? kind;
}

// --- Regler (`settings`) ------------------------------------------------------------------

/**
 * Die acht festen Keys aus `PROCESS_SETTING_KEYS`. `art` sagt, welches Eingabefeld passt —
 * abgeleitet daraus, wie der Master den Wert TATSÄCHLICH auswertet (`CaseService`), nicht
 * aus dem Schema (das lässt überall string|number|boolean zu).
 *
 * Zwei Keys sind bis heute rein deklariert (`notificationVerbosity`, `maxNudges`) — das
 * steht in ihrer Beschreibung, statt eine Wirkung zu behaupten, die es nicht gibt.
 */
export const REGLER = [
  {
    key: "slaDefault",
    label: "Frist für Freigaben",
    art: "text" as const,
    platzhalter: "z.B. 24h",
    info: "Wie lange darf eine Freigabe offen stehen, bevor gemeldet wird? Form: 90m, 24h, 3d — oder eine blosse Zahl (Minuten). Ein Schritt mit eigener Frist schlägt diesen Wert.",
  },
  {
    key: "askTimeout",
    label: "Frist für Rückfragen (Minuten)",
    art: "zahl" as const,
    platzhalter: "z.B. 240",
    info: "Nach wie vielen Minuten gilt eine unbeantwortete Rückfrage als überfällig? Ohne Angabe wird nie gemeldet — eine Rückfrage darf tagelang offen stehen.",
  },
  {
    key: "escalationRole",
    label: "Eskalation an",
    art: "rolle" as const,
    platzhalter: "",
    info: "An welche Fach-Rolle geht die Meldung, wenn eine Frist reisst? Muss eine im Prozess deklarierte Rolle sein.",
  },
  {
    key: "caseBudgetUsd",
    label: "Kostendeckel je Vorgang (USD)",
    art: "zahl" as const,
    platzhalter: "z.B. 5",
    info: "Bewusst ohne Vorbelegung: ein Deckel pro Vorgang ist etwas anderes als einer pro Turn — reisst er mitten drin, steht ein halb erledigter Prozess. Wer ihn will, setzt ihn bewusst.",
  },
  {
    key: "notificationVerbosity",
    label: "Ausführlichkeit der Meldungen",
    art: "text" as const,
    platzhalter: "",
    info: "In v1 nur deklariert — der Wert steht in der Definition, ausgewertet wird er noch nicht.",
  },
  {
    key: "maxAttempts",
    label: "Zustellversuche je Schritt",
    art: "zahl" as const,
    platzhalter: "z.B. 3",
    info: "Wie oft darf ein Schritt neu zugestellt werden, bevor der Lauf blockiert? Ohne Angabe gelten 3; Werte unter 1 werden auf 1 angehoben.",
  },
  {
    key: "maxNudges",
    label: "Erinnerungen je Rückfrage",
    art: "zahl" as const,
    platzhalter: "",
    info: "In v1 nur deklariert — der Wert steht in der Definition, ausgewertet wird er noch nicht.",
  },
  {
    key: "caseRetentionDays",
    label: "Aufbewahrung (Tage)",
    art: "zahl" as const,
    platzhalter: "z.B. 90",
    info: "Wie lange werden Vorgänge dieses Prozesses aufbewahrt? Eine Einrichtung darf den Wert nur verkürzen, nie verlängern.",
  },
] as const;

export type ReglerArt = (typeof REGLER)[number]["art"];
export type ReglerKey = (typeof REGLER)[number]["key"];

export function reglerLabel(key: string): string {
  return REGLER.find((r) => r.key === key)?.label ?? key;
}

export function reglerArt(key: string): ReglerArt | null {
  return REGLER.find((r) => r.key === key)?.art ?? null;
}

/**
 * Welchen JS-Typ erwartet ein Regler? Die INSTANZ-Grenze des Masters vergleicht `typeof`
 * gegen den deklarierten Wert — deshalb muss die Maske denselben Typ schreiben, den sie
 * anzeigt.
 */
export function erwarteterTyp(art: ReglerArt): "string" | "number" {
  return art === "zahl" ? "number" : "string";
}

// --- Lesen: die sechs Abschnitte + der Kontext, den sie brauchen ---------------------------

/** Eine Zeile behält ihr Roh-Objekt — nur die editierten Felder werden darübergelegt. */
export interface SystemZeile {
  readonly schluessel: string;
  readonly roh: unknown;
  connectorId: string;
  access: string;
  zweck: string;
}

export interface WeisungZeile {
  readonly schluessel: string;
  readonly roh: unknown;
  id: string;
  autoritaet: string;
  text: string;
  quelle: string;
}

export interface ReglerZeile {
  readonly roh: unknown;
  gesetzt: boolean;
  wert: string;
  overridable: boolean;
  /** Der vorhandene Wert hat einen anderen Typ als die Maske anbietet → nur lesbar. */
  readonly fremdTyp: boolean;
  /**
   * Der EINTRAG selbst ist kein Objekt (`"caseBudgetUsd": 5`, `null`, eine Liste) — also
   * nicht einmal `{value, overridable}`.
   *
   * ⚠ Der Unterschied zu `fremdTyp` ist der Grund, warum es dieses Feld gibt (Review H1):
   * bei `fremdTyp` liegt ein Objekt vor, dessen `value` nur die falsche Form hat — das
   * lässt sich patchen (`{...roh, overridable}`), der Wert überlebt. Bei `formFremd` gibt
   * es nichts zu patchen: `mitFeldern(5, {overridable})` ergäbe `{overridable}` und die 5
   * wäre WEG. Solche Einträge werden deshalb gar nicht erst geschrieben (der rohe Eintrag
   * wird von `mitFeldern` unverändert durchgereicht) und die Zeile ist vollständig gesperrt.
   */
  readonly formFremd: boolean;
}

export interface SpecEntwurf {
  triggerKinds: string[];
  /** `null` = der Abschnitt steht nicht im Dokument. */
  instantiation: string[] | null;
  systeme: SystemZeile[];
  eingabenPflicht: string[];
  eingabenOptional: string[];
  /** Stand `input` im Dokument? Sonst wird der Abschnitt nur bei einer Auswahl angelegt. */
  eingabenVorhanden: boolean;
  weisungen: WeisungZeile[];
  regler: Record<string, ReglerZeile>;
  /** Schlüssel in `settings`, die keiner der acht festen sind — sie bleiben, werden aber benannt. */
  fremdeRegler: string[];
  /** Abschnitte, deren Roh-Form diese Maske nicht darstellen kann — sie werden NICHT geschrieben. */
  nichtDarstellbar: AbschnittKey[];
  /** Zähler für neue Zeilen-Schlüssel (nie im Dokument; nur React-`key`). */
  seq: number;
}

export interface SpecKontext {
  rollen: string[];
  casefields: Array<{ key: string; label: string; type: string }>;
  schritte: Array<{ id: string; name: string; systeme: string[]; weisungRefs: string[] }>;
}

function textFeld(roh: unknown, key: string): string {
  const v = istRecord(roh) ? roh[key] : undefined;
  return typeof v === "string" ? v : "";
}

function stringListe(wert: unknown): string[] | null {
  if (!Array.isArray(wert)) return null;
  // Nicht-Strings dürfen NICHT stillschweigend wegfallen: die Maske schreibt die Liste
  // ganz zurück, ein gefilterter Eintrag wäre ein gelöschter Eintrag.
  if (!wert.every((e) => typeof e === "string")) return null;
  return wert as string[];
}

/**
 * `mcp__<connectorId>__<werkzeug>` → `connectorId`; alles andere unverändert.
 * Spiegel von `toolEntrySystemId` im Master — hier nur, um zu WARNEN, bevor eine gelöschte
 * System-Zeile die Definition ungültig macht. Das Urteil fällt weiterhin der Master.
 */
export function toolSystemId(eintrag: string): string {
  if (eintrag.startsWith("mcp__")) {
    const teile = eintrag.split("__");
    if (teile.length >= 3 && teile[1]) return teile[1];
  }
  return eintrag;
}

export function leseKontext(doc: Record<string, unknown>): SpecKontext {
  const rollen = stringListe(doc.rollen) ?? [];
  const casefields = Array.isArray(doc.casefields)
    ? doc.casefields
        .filter(istRecord)
        .map((f) => ({
          key: typeof f.key === "string" ? f.key : "",
          label: typeof f.label === "string" ? f.label : "",
          type: typeof f.type === "string" ? f.type : "string",
        }))
        .filter((f) => f.key !== "")
    : [];
  const schritte = Array.isArray(doc.steps)
    ? doc.steps.filter(istRecord).map((s) => ({
        id: typeof s.id === "string" ? s.id : "",
        name: typeof s.name === "string" ? s.name : "",
        systeme: (stringListe(s.tools) ?? []).map(toolSystemId),
        weisungRefs: stringListe(s.weisungRefs) ?? [],
      }))
    : [];
  return { rollen, casefields, schritte };
}

export function leseEntwurf(doc: Record<string, unknown>): SpecEntwurf {
  const nichtDarstellbar: AbschnittKey[] = [];
  let seq = 0;
  const naechsterSchluessel = (): string => `z${++seq}`;

  // trigger.kinds
  let triggerKinds: string[] = [];
  const trigger = doc.trigger;
  if (trigger === undefined) {
    triggerKinds = [];
  } else if (istRecord(trigger) && (trigger.kinds === undefined || stringListe(trigger.kinds) !== null)) {
    triggerKinds = stringListe(trigger.kinds) ?? [];
  } else {
    nichtDarstellbar.push("trigger");
  }

  // instantiation.roles
  let instantiation: string[] | null = null;
  const inst = doc.instantiation;
  if (inst === undefined) {
    instantiation = null;
  } else if (istRecord(inst) && stringListe(inst.roles) !== null) {
    instantiation = stringListe(inst.roles);
  } else {
    nichtDarstellbar.push("instantiation");
  }

  // systems[]
  let systeme: SystemZeile[] = [];
  if (doc.systems === undefined) {
    systeme = [];
  } else if (Array.isArray(doc.systems) && doc.systems.every(istRecord)) {
    systeme = doc.systems.map((s) => ({
      schluessel: naechsterSchluessel(),
      roh: s,
      connectorId: textFeld(s, "connectorId"),
      access: textFeld(s, "access") || "read",
      zweck: textFeld(s, "zweck"),
    }));
  } else {
    nichtDarstellbar.push("systems");
  }

  // input
  let eingabenPflicht: string[] = [];
  let eingabenOptional: string[] = [];
  let eingabenVorhanden = false;
  const input = doc.input;
  if (input === undefined) {
    eingabenVorhanden = false;
  } else if (
    istRecord(input) &&
    (input.required === undefined || stringListe(input.required) !== null) &&
    (input.optional === undefined || stringListe(input.optional) !== null)
  ) {
    eingabenVorhanden = true;
    eingabenPflicht = stringListe(input.required) ?? [];
    eingabenOptional = stringListe(input.optional) ?? [];
  } else {
    nichtDarstellbar.push("input");
  }

  // weisungen[]
  let weisungen: WeisungZeile[] = [];
  if (doc.weisungen === undefined) {
    weisungen = [];
  } else if (Array.isArray(doc.weisungen) && doc.weisungen.every(istRecord)) {
    weisungen = doc.weisungen.map((w) => ({
      schluessel: naechsterSchluessel(),
      roh: w,
      id: textFeld(w, "id"),
      autoritaet: textFeld(w, "autoritaet") || "verbindlich",
      text: textFeld(w, "text"),
      quelle: textFeld(w, "quelle"),
    }));
  } else {
    nichtDarstellbar.push("weisungen");
  }

  // settings
  const regler: Record<string, ReglerZeile> = {};
  const rohSettings = doc.settings;
  const settingsDarstellbar = rohSettings === undefined || istRecord(rohSettings);
  if (!settingsDarstellbar) nichtDarstellbar.push("settings");
  for (const r of REGLER) {
    const eintrag = istRecord(rohSettings) ? rohSettings[r.key] : undefined;
    // ⚠ Review H1: die EINTRÄGE prüfen, nicht nur den Abschnitt. `settings` kann ein
    // Objekt sein und trotzdem einen Skalar (`"caseBudgetUsd": 5`) oder `null` tragen.
    const formFremd = eintrag !== undefined && !istRecord(eintrag);
    const wertRoh = istRecord(eintrag) ? eintrag.value : undefined;
    const passt = typeof wertRoh === erwarteterTyp(r.art);
    regler[r.key] = {
      roh: eintrag,
      gesetzt: eintrag !== undefined,
      wert: passt ? String(wertRoh) : "",
      overridable: istRecord(eintrag) && eintrag.overridable === true,
      fremdTyp: eintrag !== undefined && !passt,
      formFremd,
    };
  }
  // Schlüssel in `settings`, die keiner der acht sind. Sie überleben (patch-over-raw), aber
  // sie sollen nicht unsichtbar sein — der Master lehnt die Definition ihretwegen ab.
  const fremdeRegler = istRecord(rohSettings)
    ? Object.keys(rohSettings).filter((k) => !REGLER.some((r) => r.key === k))
    : [];

  return {
    fremdeRegler,
    triggerKinds,
    instantiation,
    systeme,
    eingabenPflicht,
    eingabenOptional,
    eingabenVorhanden,
    weisungen,
    regler,
    nichtDarstellbar,
    seq,
  };
}

// --- Schreiben: Entwurf → Abschnitts-Ersetzungen -------------------------------------------

/** Nur `roles`/`value`-Felder drin? Dann ist ein leerer Abschnitt restlos entfernbar. */
function nurFelder(roh: unknown, erlaubt: string[]): boolean {
  if (roh === undefined) return true;
  if (!istRecord(roh)) return false;
  return Object.keys(roh).every((k) => erlaubt.includes(k));
}

function gleicheListe(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Die im Dokument stehenden Instanziierungs-Rollen (tolerant, ohne Urteil). */
function leseRollenListe(doc: Record<string, unknown>): string[] {
  return istRecord(doc.instantiation) ? (stringListe(doc.instantiation.roles) ?? []) : [];
}

export function baueAbschnitte(entwurf: SpecEntwurf, doc: Record<string, unknown>): Record<string, PatchWert> {
  const ersatz: Record<string, PatchWert> = {};
  const uebersprungen = new Set<AbschnittKey>(entwurf.nichtDarstellbar);

  if (!uebersprungen.has("trigger")) {
    ersatz.trigger = mitFeldern(doc.trigger, { kinds: [...entwurf.triggerKinds] });
  }

  if (!uebersprungen.has("instantiation")) {
    const rollen = entwurf.instantiation ?? [];
    // ⚠ Review M1: entfernt wird nur, wenn jemand die Liste tatsächlich GELEERT hat. Stand
    // dort schon `{roles:[]}`, wäre das Entfernen zwar bedeutungsgleich, aber ein Diff, den
    // niemand verlangt hat — und der Speichern-Knopf stünde nach dem blossen Öffnen offen.
    const warSchonLeer = doc.instantiation !== undefined && rollen.length === 0 && leseRollenListe(doc).length === 0;
    if (rollen.length === 0 && !warSchonLeer && nurFelder(doc.instantiation, ["roles"])) {
      ersatz.instantiation = ENTFERNEN;
    } else {
      ersatz.instantiation = mitFeldern(doc.instantiation, { roles: rollen });
    }
  }

  if (!uebersprungen.has("systems")) {
    // ⚠ Review L2: NICHT getrimmt geschrieben. Der Master trimmt nicht mit; ein Trim wäre
    // eine stille Wertänderung — und beim blossen Öffnen einer Datei mit Leerraum ein Diff.
    // Getrimmt wird nur ENTSCHIEDEN (leer ⇒ Schlüssel weg).
    ersatz.systems = entwurf.systeme.map((s) =>
      mitFeldern(s.roh, {
        connectorId: s.connectorId,
        access: s.access,
        zweck: s.zweck.trim() === "" ? ENTFERNEN : s.zweck,
      }),
    );
  }

  if (!uebersprungen.has("input")) {
    const pflicht = [...entwurf.eingabenPflicht];
    const optional = [...entwurf.eingabenOptional];
    const rohInput = istRecord(doc.input) ? doc.input : undefined;
    const rohPflicht = stringListe(rohInput?.required) ?? [];
    const rohOptional = stringListe(rohInput?.optional) ?? [];
    // ⚠ Review M1: nur schreiben, wenn sich wirklich etwas unterscheidet. Sonst bekäme eine
    // handgeschriebene Form (`{"required":["x"]}`) beim blossen Öffnen ein `"optional": []`
    // dazu — bedeutungsgleich, aber nicht byte-gleich, und damit ein Phantom-Diff.
    if (!gleicheListe(pflicht, rohPflicht) || !gleicheListe(optional, rohOptional)) {
      ersatz.input = mitFeldern(doc.input, {
        // Einen leeren Schlüssel, den es vorher nicht gab, legt die Maske nicht an.
        required: pflicht.length > 0 || (rohInput !== undefined && Object.hasOwn(rohInput, "required")) ? pflicht : ENTFERNEN,
        optional: optional.length > 0 || (rohInput !== undefined && Object.hasOwn(rohInput, "optional")) ? optional : ENTFERNEN,
      });
    }
  }

  if (!uebersprungen.has("weisungen")) {
    ersatz.weisungen = entwurf.weisungen.map((w) =>
      mitFeldern(w.roh, {
        // Die Kennung ist ein Slug — dort ist Leerraum nie gewollt und im Dokument auch
        // nie gültig; der Text dagegen gehört dem Autor (Review L2).
        id: w.id.trim(),
        autoritaet: w.autoritaet,
        text: w.text,
        quelle: w.quelle.trim() === "" ? ENTFERNEN : w.quelle,
      }),
    );
  }

  if (!uebersprungen.has("settings")) {
    const felder: Record<string, PatchWert> = {};
    for (const r of REGLER) {
      const zeile = entwurf.regler[r.key];
      if (!zeile) continue;
      // ⚠ Review H1: ein Eintrag, der gar kein Objekt ist (`"caseBudgetUsd": 5`, `null`),
      // wird ÜBERHAUPT NICHT gepatcht. `mitFeldern(5, …)` ergäbe `{overridable:false}` —
      // der Wert wäre zerstört. Ohne Eintrag in `felder` reicht `mitFeldern(doc.settings, …)`
      // den rohen Wert unverändert durch; die Zeile ist in der Maske gesperrt.
      if (zeile.formFremd) continue;
      if (!zeile.gesetzt) {
        felder[r.key] = ENTFERNEN;
        continue;
      }
      if (zeile.fremdTyp) {
        // Ein Objekt, dessen `value` eine andere Form hat: das lässt sich patchen, der Wert
        // überlebt — der Freigabe-Schalter darf deshalb umgelegt werden.
        felder[r.key] = mitFeldern(zeile.roh, { overridable: zeile.overridable });
        continue;
      }
      // Review L2: Text-Werte ungetrimmt (der Master trimmt nicht); für die ZAHL ist der
      // Trim folgenlos, weil `Number(" 5 ")` ohnehin 5 ergibt.
      const wert: string | number = r.art === "zahl" ? Number(zeile.wert.trim()) : zeile.wert;
      felder[r.key] = mitFeldern(zeile.roh, { value: wert, overridable: zeile.overridable });
    }
    const neu = mitFeldern(doc.settings, felder);
    if (Object.keys(neu).length === 0 && doc.settings === undefined) {
      // Kein Regler gesetzt und vorher stand nichts da ⇒ keinen leeren Abschnitt anlegen.
    } else {
      ersatz.settings = neu;
    }
  }

  return ersatz;
}

// --- Prävention: was der Master ablehnen WÜRDE ---------------------------------------------

export interface Befund {
  /** `fehler` sperrt das Speichern, `hinweis` informiert nur. */
  art: "fehler" | "hinweis";
  abschnitt: AbschnittKey;
  text: string;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function pruefeEntwurf(
  entwurf: SpecEntwurf,
  kontext: SpecKontext,
  bekannteConnectorIds: string[] | null,
): Befund[] {
  const befunde: Befund[] = [];
  const uebersprungen = new Set<AbschnittKey>(entwurf.nichtDarstellbar);
  const fehler = (abschnitt: AbschnittKey, text: string): void => {
    befunde.push({ art: "fehler", abschnitt, text });
  };
  const hinweis = (abschnitt: AbschnittKey, text: string): void => {
    befunde.push({ art: "hinweis", abschnitt, text });
  };

  // 1 — Auslöser: mindestens einer, ohne Doppel (Doppel sind durch die Maske ausgeschlossen).
  if (!uebersprungen.has("trigger") && entwurf.triggerKinds.length === 0) {
    fehler("trigger", "Mindestens ein Auslöser muss erlaubt sein.");
  }

  // 2 — Instanziierungs-Rechte ⊆ deklarierte Rollen.
  if (!uebersprungen.has("instantiation")) {
    for (const r of entwurf.instantiation ?? []) {
      if (!kontext.rollen.includes(r)) {
        fehler("instantiation", `„${r}" ist keine im Prozess deklarierte Fach-Rolle.`);
      }
    }
  }

  // 3 — Systeme: Zielsystem vorhanden, keine Doppel, nichts wegnehmen, was ein Schritt nutzt.
  if (!uebersprungen.has("systems")) {
    const gesehen = new Set<string>();
    for (const s of entwurf.systeme) {
      const id = s.connectorId.trim();
      if (id === "") {
        fehler("systems", "Eine Zeile hat noch kein Zielsystem.");
        continue;
      }
      if (gesehen.has(id)) fehler("systems", `Das System „${id}" steht doppelt in der Liste.`);
      gesehen.add(id);
      // Review M3: der Zweck ist ein Feld DIESER Maske — also gilt hier auch ihr Deckel.
      // (Die `connectorId` kommt aus einem Auswahlfeld, nie aus freier Eingabe; eine
      // Slug-Prüfung wäre eine Aussage über Bestand, den die Maske nicht erzeugen kann.)
      if (s.zweck.trim().length > 500) {
        fehler("systems", `Die Zweck-Angabe zu „${id}" ist länger als 500 Zeichen.`);
      }
      if (bekannteConnectorIds !== null && !bekannteConnectorIds.includes(id)) {
        hinweis("systems", `Das System „${id}" steht nicht (mehr) im Katalog — die Bereitschaft meldet das als Lücke.`);
      }
    }
    for (const schritt of kontext.schritte) {
      for (const genutzt of schritt.systeme) {
        if (!gesehen.has(genutzt)) {
          fehler(
            "systems",
            `Schritt „${schritt.name || schritt.id}" arbeitet mit „${genutzt}" — solange das dort steht, muss das System hier deklariert sein.`,
          );
        }
      }
    }
  }

  // 4 — Start-Eingaben ⊆ Vorgangsfelder (disjunkt ist durch die Maske gesichert).
  if (!uebersprungen.has("input")) {
    const bekannt = new Set(kontext.casefields.map((f) => f.key));
    for (const k of [...entwurf.eingabenPflicht, ...entwurf.eingabenOptional]) {
      if (!bekannt.has(k)) {
        hinweis("input", `„${k}" ist kein Vorgangsfeld dieses Prozesses — der Master lehnt die Definition deshalb ab.`);
      }
    }
  }

  // 5 — Weisungen: Kennung, Text, keine Doppel, nichts wegnehmen, worauf ein Schritt zeigt.
  if (!uebersprungen.has("weisungen")) {
    const ids = new Set<string>();
    for (const w of entwurf.weisungen) {
      const id = w.id.trim();
      if (id === "") {
        fehler("weisungen", "Eine Weisung hat noch keine Kennung.");
      } else if (!SLUG_RE.test(id)) {
        fehler("weisungen", `Die Kennung „${id}" ist nicht zulässig (Kleinbuchstaben, Ziffern, Bindestriche).`);
      } else if (ids.has(id)) {
        fehler("weisungen", `Die Kennung „${id}" ist doppelt vergeben.`);
      }
      if (id !== "") ids.add(id);
      if (w.text.trim() === "") fehler("weisungen", `Die Weisung „${id || "ohne Kennung"}" hat keinen Text.`);
      if (w.text.trim().length > 2000) fehler("weisungen", `Der Text von „${id}" ist länger als 2000 Zeichen.`);
      // Review M3: auch die Quelle ist ein Feld dieser Maske.
      if (w.quelle.trim().length > 500) {
        fehler("weisungen", `Die Quelle von „${id}" ist länger als 500 Zeichen.`);
      }
    }
    for (const schritt of kontext.schritte) {
      for (const ref of schritt.weisungRefs) {
        if (!ids.has(ref)) {
          fehler(
            "weisungen",
            `Schritt „${schritt.name || schritt.id}" beruft sich auf „${ref}" — solange das dort steht, muss die Weisung hier stehen.`,
          );
        }
      }
    }
  }

  // 6 — Regler: Wert vorhanden, Zahl lesbar, Eskalations-Rolle deklariert.
  if (!uebersprungen.has("settings")) {
    for (const r of REGLER) {
      const zeile = entwurf.regler[r.key];
      if (!zeile?.gesetzt) continue;
      // Review H1: ein Eintrag ohne Objekt-Form wird nicht angetastet — aber er wird
      // BENANNT. Sonst stünde ein Regler da, den der Master beanstandet, und niemand
      // erführe warum (der Master antwortet: an dieser Stelle wird ein Objekt erwartet).
      if (zeile.formFremd) {
        hinweis(
          "settings",
          `„${r.label}" steht nicht in der erwarteten Form (erwartet wird ein Eintrag mit Wert und Freigabe) — er bleibt unverändert, der Master lehnt die Definition deshalb ab.`,
        );
        continue;
      }
      if (zeile.fremdTyp) continue;
      const roh = zeile.wert.trim();
      if (roh === "") {
        fehler("settings", `„${r.label}" ist eingeschaltet, aber ohne Wert.`);
        continue;
      }
      // Review L2/L7: der Deckel gilt auf dem WERT, der geschrieben wird (ungetrimmt).
      if (r.art !== "zahl" && zeile.wert.length > 500) {
        fehler("settings", `„${r.label}" ist länger als 500 Zeichen.`);
      }
      if (r.art === "zahl" && !Number.isFinite(Number(roh))) {
        fehler("settings", `„${r.label}" braucht eine Zahl.`);
      }
      if (r.art === "rolle" && !kontext.rollen.includes(zeile.wert)) {
        fehler("settings", `„${r.label}": „${roh}" ist keine im Prozess deklarierte Fach-Rolle.`);
      }
    }
    if (entwurf.regler.escalationRole?.gesetzt && kontext.rollen.length === 0) {
      hinweis("settings", "Dieser Prozess deklariert noch keine Fach-Rollen — ohne sie gibt es niemanden zum Eskalieren.");
    }
    // Review L5: unbekannte Schlüssel im Regler-Abschnitt benennen (Muster: fremde Auslöser).
    if (entwurf.fremdeRegler.length > 0) {
      hinweis(
        "settings",
        `Im Regler-Abschnitt stehen ausserdem unbekannte Schlüssel: ${entwurf.fremdeRegler.join(", ")}. Sie bleiben stehen — der Master lehnt die Definition damit ab.`,
      );
    }
  }

  return befunde;
}

export function hatFehler(befunde: Befund[]): boolean {
  return befunde.some((b) => b.art === "fehler");
}

/**
 * Darf jetzt gespeichert werden? EINE Regel für ZWEI Schlösser (Review L6, Muster #324):
 * der gesperrte Knopf ist die Anzeige, der `return` im Handler die Durchsetzung. Stünde die
 * Bedingung zweimal ausgeschrieben, liefe sie irgendwann auseinander — und ausgerechnet der
 * Handler ist die Kopie, die niemand ansieht.
 */
export function darfSpeichern(lage: { gesperrt: boolean; fehler: boolean; unveraendert: boolean }): boolean {
  return !lage.gesperrt && !lage.fehler && !lage.unveraendert;
}

// --- Instanz-Seite (UC-2): dieselben Regeln, andere Richtung -------------------------------

export interface OverrideDeklaration {
  value: string | number | boolean;
  overridable: boolean;
}

export type OverridePruefung =
  | { readonly ok: true; readonly wert: string | number | boolean | null }
  | { readonly ok: false; readonly fehler: string };

/**
 * Ein Override-Eingabefeld → der Wert, der ans PATCH geht. `null` = nichts gesetzt (die
 * Vorlage gilt).
 *
 * Spiegelt `ProcessInstanceService.validateAgainstSpec` Regel für Regel — nicht um den
 * Master zu ersetzen (er bleibt der Richter), sondern damit niemand in ein generisches
 * 400 läuft, dessen Grund nur im Quelltext steht.
 */
export function pruefeOverride(
  key: string,
  eingabe: string,
  deklaration: OverrideDeklaration,
  rollen: string[],
): OverridePruefung {
  const label = reglerLabel(key);
  const roh = eingabe.trim();
  if (roh === "") return { ok: true, wert: null };

  const typ = typeof deklaration.value;
  if (typ === "number") {
    const n = Number(roh);
    if (!Number.isFinite(n)) return { ok: false, fehler: `„${label}" braucht eine Zahl.` };
    // Enger/gleich, nie weiter (Konzept §7) — der Master lehnt beides ab.
    if (key === "caseBudgetUsd" && n > (deklaration.value as number)) {
      return { ok: false, fehler: `„${label}" darf höchstens ${deklaration.value} sein — die Vorlage erlaubt nur weniger.` };
    }
    if (key === "caseRetentionDays" && n > (deklaration.value as number)) {
      return { ok: false, fehler: `„${label}" darf höchstens ${deklaration.value} sein — die Vorlage erlaubt nur kürzer.` };
    }
    return { ok: true, wert: n };
  }
  if (typ === "boolean") {
    if (roh !== "true" && roh !== "false") return { ok: false, fehler: `„${label}" braucht true oder false.` };
    return { ok: true, wert: roh === "true" };
  }
  if (key === "escalationRole" && !rollen.includes(roh)) {
    return { ok: false, fehler: `„${label}": „${roh}" ist keine im Prozess deklarierte Fach-Rolle.` };
  }
  // Review L7: Wire-Deckel des Masters (`primitiveValue` im ProcessInstanceDto).
  if (roh.length > MAX_PRIMITIVE_LAENGE) {
    return { ok: false, fehler: `„${label}" ist länger als ${MAX_PRIMITIVE_LAENGE} Zeichen.` };
  }
  return { ok: true, wert: roh };
}

/** Deckel eines einzelnen Wertes im Instanz-PATCH (`primitiveValue`, ProcessInstanceDto). */
export const MAX_PRIMITIVE_LAENGE = 2000;
/** Deckel der Zeitzone und des Prompt-Textes im Trigger (`triggerSchema`). */
export const MAX_TIMEZONE_LAENGE = 64;
export const MAX_PROMPTTEXT_LAENGE = 4000;

/** Cron-Form der Einrichtung: der Master verlangt GENAU fünf Felder. */
export function cronFehler(cron: string): string | null {
  const roh = cron.trim();
  if (roh === "") return "Ein Zeitplan braucht eine Cron-Angabe.";
  if (roh.split(/\s+/).length !== 5) return "Die Cron-Angabe braucht genau fünf Felder (z.B. 0 8 * * 1).";
  if (roh.length > 100) return "Die Cron-Angabe ist zu lang.";
  return null;
}
